import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import { encodeData, decodeData } from '@/utils';
import { toast } from 'sonner';
import { getOnboardingResumeTarget, readPendingInviteFromSession } from '@/utils/onboardingResume';

export default function RegisterCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuthType } = useAuthStore();
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const handleCallback = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;

        if (!session?.user) {
          const { data } = supabase.auth.onAuthStateChange(async (event, sess) => {
            if (!mounted) return;
            if (event === 'SIGNED_IN' && sess?.user) {
              data.subscription.unsubscribe();
              await resolveRedirect(sess.user);
            }
          });

          setTimeout(() => {
            if (mounted) {
              setAuthType('signup');
              navigate('/signup');
            }
          }, 5000);
          return;
        }

        await resolveRedirect(session.user);
      } catch (err) {
        console.error('Register callback error:', err);
        if (mounted) {
          setError(err.message);
          setTimeout(() => navigate('/signup'), 3000);
        }
      }
    };

    const resolveRedirect = async (user) => {
      if (!mounted) return;

      // Existing-user dup guard — OAuth signup that discovers an
      // existing BE profile pushes the user back to /signin (product
      // decision — avoids the "I meant to sign in but hit signup"
      // confusion). Unchanged from previous behaviour.
      try {
        const existingUser = await apiClient.getUserByUserId(user.id);
        if (existingUser) {
          toast.error('This email is already registered. Please sign in instead.');
          await supabase.auth.signOut();
          navigate('/');
          setTimeout(() => setAuthType('signin'), 100);
          return;
        }
      } catch {
        // User not found — this is expected for new registrations
      }

      // New user — route via the resume helper. For fresh OAuth
      // signups the helper resolves to { path: '/onboarding',
      // step: 'profile' }; keep the ?d= payload so the userType
      // selection carries through (matches the previous
      // progress=pending behaviour).
      const { data: { session } } = await supabase.auth.getSession();
      const target = getOnboardingResumeTarget(null, null, {
        pendingInvite: readPendingInviteFromSession(session),
      });
      if (target.path === '/onboarding') {
        const inviteData = decodeData(searchParams.get('d'));
        const userTypes = inviteData?.userTypes || [];
        navigate(`/onboarding?d=${encodeURIComponent(encodeData({ userTypes, progress: 'pending' }))}`);
        return;
      }
      navigate(target.path);
    };

    handleCallback();

    return () => {
      mounted = false;
    };
  }, [navigate, setAuthType, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <p className="text-red-500 text-sm">{error}</p>
        ) : (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Registering your account...</p>
          </>
        )}
      </div>
    </div>
  );
}
